import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { useTypewriterReveal } from "@/features/chat/hooks/use-typewriter-reveal"
import { cn } from "@/lib/utils"

type ThinkingPanelProps = {
  thinking: string
  /** True while still accumulating (pre-plan-approval). Defaults panel open. */
  isActive: boolean
  /**
   * True while thinking tokens are still streaming in. Gates the typewriter
   * reveal; when false the full text is shown immediately.
   */
  isStreaming?: boolean
}

export function ThinkingPanel({ thinking, isActive, isStreaming = false }: ThinkingPanelProps) {
  const { t } = useTranslation()
  const revealed = useTypewriterReveal(thinking, isStreaming)
  if (!thinking) return null

  return (
    <Accordion
      collapsible
      className="border-muted-foreground/30 bg-muted/30 my-2 rounded-md border border-dashed px-3"
      defaultValue={isActive ? "thinking" : undefined}
      type="single"
    >
      <AccordionItem className="border-b-0" value="thinking">
        <AccordionTrigger className="text-muted-foreground py-2 text-xs font-medium hover:no-underline">
          {t("chat.thinking_panel.label", { defaultValue: "Thinking" })}
        </AccordionTrigger>
        <AccordionContent>
          <div
            className={cn(
              "prose dark:prose-invert text-muted-foreground max-w-none text-sm",
              "prose-p:my-2 prose-li:my-0"
            )}
          >
            {isStreaming ? (
              <pre className="font-inherit whitespace-pre-wrap">{revealed}</pre>
            ) : (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{revealed}</ReactMarkdown>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
